<template>
    <div class="cf-toolbar">
        <div class="cf-search">
            <i class="fal fa-search cf-search__icon"></i>
            <input
                type="text"
                v-model="search"
                :placeholder="$t('company.citizens.search_placeholder')"
                class="cf-search__input"
                @keydown.enter="applyFilters"
            />
            <button
                v-if="search"
                type="button"
                class="cf-search__clear"
                :title="$t('common.reset')"
                @click="clearSearch"
            >
                <i class="fal fa-times"></i>
            </button>
        </div>

        <div class="cf-role">
            <i class="fal fa-user-tag cf-role__icon"></i>
            <select v-model="role" class="cf-role__select" @change="applyFilters">
                <option value="">{{ $t('company.citizens.all_roles') }}</option>
                <option
                    v-for="option in roleOptions"
                    :key="option"
                    :value="option"
                >
                    {{ $t('company.citizens.roles.' + option) }}
                </option>
            </select>
            <i class="fal fa-chevron-down cf-role__caret"></i>
        </div>

        <button type="button" class="cf-btn-search" @click="applyFilters">
            <i class="fal fa-filter"></i>
            <span>{{ $t('company.citizens.apply_filters') }}</span>
        </button>

        <router-link v-if="canCreate" to="/company/citizens/create" class="cf-btn-create">
            <i class="fal fa-plus"></i>
            <span>{{ $t('company.citizens.create_one') }}</span>
        </router-link>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapState } from 'vuex'
import { CITIZEN_ROLES } from '@/config/citizenRoles'

export default {
    name: 'CitizensFilters',
    data() {
        return {
            search: '',
            role: '',
            roleOptions: CITIZEN_ROLES
        }
    },
    computed: {
        ...mapState('company', {
            config: (state) => state.citizensConfig
        }),
        canCreate() {
            const permissions = this.$store.state.auth.permissions || []
            return permissions.includes('users.create')
        }
    },
    created() {
        this.search = this.config.search || ''
        this.role = this.config.role || ''
    },
    methods: {
        applyFilters() {
            this.config.page = 1
            this.config.search = this.search
            this.config.role = this.role
            this.$emit('on-filter-change')
        },
        clearSearch() {
            this.search = ''
            this.applyFilters()
        }
    }
}
</script>

<style scoped>
.cf-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
}

.cf-search {
    position: relative;
    flex: 1;
    min-width: 0;
}

.cf-search__icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 0.8rem;
    pointer-events: none;
}

.cf-search__input {
    width: 100%;
    box-sizing: border-box;
    padding: 9px 34px 9px 34px;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    font-size: 0.84rem;
    color: #0f172a;
    background: #f8fafc;
    outline: none;
    transition: all 0.2s;
}

.cf-search__input::placeholder {
    color: #94a3b8;
}

.cf-search__input:hover {
    border-color: #cbd5e1;
}

.cf-search__input:focus {
    border-color: #0891B2;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.1);
}

.cf-search__clear {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 22px;
    height: 22px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    font-size: 0.75rem;
    line-height: 1;
}

.cf-search__clear:hover {
    background: #e2e8f0;
    color: #475569;
}

/* Role filter */
.cf-role {
    position: relative;
    flex-shrink: 0;
    min-width: 220px;
}

.cf-role__icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 0.8rem;
    pointer-events: none;
}

.cf-role__caret {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 0.7rem;
    pointer-events: none;
}

.cf-role__select {
    width: 100%;
    box-sizing: border-box;
    padding: 9px 30px 9px 34px;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    font-size: 0.84rem;
    color: #0f172a;
    background: #f8fafc;
    outline: none;
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;
    transition: all 0.2s;
}

.cf-role__select:hover {
    border-color: #cbd5e1;
}

.cf-role__select:focus {
    border-color: #0891B2;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.1);
}

.cf-btn-search {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 20px;
    border: none;
    background: #0891B2;
    color: #fff !important;
    font-size: 0.85rem;
    font-weight: 600;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.2s;
    white-space: nowrap;
}

.cf-btn-search:hover {
    background: #0e7490;
}

.cf-btn-search i {
    color: #fff !important;
    font-size: 0.8rem;
}

.cf-btn-create {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 20px;
    border: 1.5px solid #0891B2;
    background: #fff;
    color: #0891B2 !important;
    font-size: 0.85rem;
    font-weight: 600;
    border-radius: 10px;
    text-decoration: none !important;
    transition: all 0.2s;
    white-space: nowrap;
}

.cf-btn-create:hover {
    background: rgba(8, 145, 178, 0.08);
}

.cf-btn-create i {
    color: #0891B2 !important;
    font-size: 0.8rem;
}

@media (max-width: 900px) {
    .cf-role {
        min-width: 180px;
    }
}

@media (max-width: 640px) {
    .cf-toolbar {
        flex-direction: column;
        align-items: stretch;
        padding: 12px 14px;
    }

    .cf-role {
        min-width: 100%;
    }

    .cf-btn-search,
    .cf-btn-create {
        justify-content: center;
    }
}
</style>
